import { customRequest } from "@/utils/custom-fetch";
import axiosClient from "@/libs/axiosClient";
import { computeParams } from "@/utils/to-params";
import { downloadXls } from "@/utils/download-xls";

enum API_URL {
  all = "/api/v1/pools/sub_account/all/",
  create = "/api/v1/pools/sub_account/create/",
  summary = "/api/v1/pools/sub_account/summary/",
  earnings = "/api/v1/pools/sub_account/earnings/",
  payouts = "/api/v1/pools/sub_account/payouts/",
  workers = "/api/v1/pools/sub_account/workers/",
  portfolioSummary = "/api/v1/pools/sub_account/portfolio_summary/",
  portfolioIncomeHistory = "/api/v1/pools/sub_account/portfolio_income_history/",
  bulkExport = "/api/v1/pools/sub_account/export/",
  currencyIncome = "/api/v1/pools/sub_account/currency_income/",
  currencyHashrateChart = "/api/v1/pools/sub_account/currency_hashrate_chart/",
  stratumUrl = "/api/v1/pools/sub_account/stratum_url/",
  statistics = "/api/v1/statistic/crypto_currency/",
  chart = "/api/v1/pools/sub_account/observer/hashrate_chart/",
  earningExportXls = "/api/v1/pools/sub_account/earnings/xls/",
  payoutExportXls = "/api/v1/pools/sub_account/payouts/xls/",
  changeWallet = "/api/v1/pools/sub_account/sub_account_update/wallet_address/",
}

export const getSubAccounts = () => {
  return customRequest.getItem(API_URL.all);
};

export const getSubAccountsSummary = (
  name: string,
  { signal }: { signal: any },
) => {
  return customRequest.getItem(
    API_URL.summary + `?sub_account_name=${name}`,
    "",
    { signal },
  );
};

export const getSubAccountsPayouts = (
  params: any,
  { signal }: { signal: any },
) => {
  return customRequest.getItem(API_URL.payouts + computeParams(params), "", {
    signal: signal,
  });
};

export const getSubAccountsWorkers = (
  params: { sub_account_name: string; worker_status: string },
  { signal }: { signal: any },
) => {
  return customRequest.getItem(API_URL.workers, computeParams(params), {
    signal: signal,
  });
};

export const getSubAccountsEarning = (
  params: any,
  { signal }: { signal: any },
) => {
  return customRequest.getItem(API_URL.earnings + computeParams(params), "", {
    signal,
  });
};

export const getPortfolioSummary = ({ signal }: { signal: any }) => {
  return customRequest.getItem(API_URL.portfolioSummary, "", { signal });
};

export const getPortfolioIncomeHistory = ({ signal }: { signal: any }) => {
  return customRequest.getItem(API_URL.portfolioIncomeHistory, "", { signal });
};

export const getCurrencyIncome = (
  currency: string,
  { signal }: { signal: any },
) => {
  return customRequest.getItem(
    API_URL.currencyIncome + `?currency=${encodeURIComponent(currency)}`,
    "",
    { signal },
  );
};

export const getCurrencyHashrateChart = (
  {
    currency,
    interval,
    subAccountNames,
  }: { currency: string; interval: string; subAccountNames?: string },
  signal?: any,
) => {
  return customRequest.getItem(
    API_URL.currencyHashrateChart +
      computeParams({
        currency,
        interval,
        sub_account_names: subAccountNames || "",
      }),
    "",
    { signal },
  );
};

export const createSubAccount = (body: any) => {
  return customRequest.post(API_URL.create, body);
};

export const editSubAccountWallet = (body: {
  sub_account_id: number;
  wallet_address: string;
}) => {
  return customRequest.patch(API_URL.changeWallet, body);
};

export const getSubAccountStratumUrls = (name: string) => {
  return customRequest.getItem(
    API_URL.stratumUrl + `?sub_account_name=${name}`,
  );
};

export const getStatistics = () => {
  return axiosClient.get(API_URL.statistics);
};

export const getSubAccountChart = (
  {
    sub_account_name,
    interval,
  }: {
    sub_account_name: string;
    interval: string;
  },
  signal?: any,
) => {
  return customRequest.getItem(
    API_URL.chart +
      `?interval=${interval}&sub_account_name=${sub_account_name}`,
    "",
    { signal },
  );
};
export const exportEarningXls = (params: {
  sub_account_name: string;
  start_date: string | null;
  end_date: string | null;
}) => {
  return customRequest
    .getItem(API_URL.earningExportXls + computeParams(params), "", {
      responseType: "blob",
    })
    .then((response: any) => {
      downloadXls(response);
    });
};

export const exportPayoutXls = (params: {
  sub_account_name: string;
  start_date: string | null;
  end_date: string | null;
}) => {
  return customRequest
    .getItem(API_URL.payoutExportXls + computeParams(params), "", {
      responseType: "blob",
    })
    .then((response: any) => {
      downloadXls(response);
    });
};

export interface BulkExportRequest {
  kind: "earnings" | "payouts";
  // Empty = every sub-account of the user.
  sub_account_names: string[];
  start_date: string;
  end_date: string;
}

export interface BulkExportJob {
  job_id: string;
  status: "PENDING" | "RUNNING" | "DONE" | "FAILED";
  download_ready?: boolean;
}

// Bulk export is a job: POST queues it, GET polls it, ?download=1 returns the
// file once it is DONE (same pattern as the admin export).
export const createBulkExport = (body: BulkExportRequest) => {
  return customRequest.post<BulkExportRequest, any>(API_URL.bulkExport, body);
};

export const getBulkExportJob = (jobId: string, { signal }: { signal: any }) => {
  return customRequest.getItem<unknown, BulkExportJob>(
    `${API_URL.bulkExport}${jobId}/`,
    "",
    { signal },
  );
};

export const downloadBulkExport = (jobId: string) => {
  return customRequest
    .getItem(`${API_URL.bulkExport}${jobId}/`, "?download=1", {
      responseType: "blob",
    })
    .then((response: any) => {
      downloadXls(response);
    });
};
